import { useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import clsx from "clsx";
import { ChevronDown, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import PdfView from "components/pdf-view/Pdf_modal";
import Notice from "components/tw/notice";
import { btnIconCls, btnPrimaryCls, helpTextCls, inputCls } from "components/tw/classes";
import { MAX_LEAVE_DAYS, leaveCalendarLeaveTypeI18nKey } from "features/leave-calendar/leaveCalendar.constants";
import { leaveCalendarDateLocale } from "features/leave-calendar/leaveCalendarDates";
import { useLanguage } from "utils/use-language";
import CustomFieldInputs, { FIELD_LABEL_CLS } from "./custom-field-inputs";
import { rangeLabel } from "./leave-range";
import RangeCalendar from "./range-calendar";
import { useLeaveRequestFlow } from "./use-leave-request-flow";

// The type chips: the first row is always shown, the second one behind «Kiti tipai».
// «business trip leave» is left out because it needs a business trip picked, which this sheet has no field for.
const MAIN_TYPES = ["vacation", "remote work", "additional day off"];
const OTHER_TYPES = ["unpaid leave", "additional rest time", "paternity leave"];

const DURATIONS = [
  { value: "full_day", labelKey: "leave_sheet_duration_full" },
  { value: "half_day", labelKey: "leave_sheet_duration_half" },
  { value: "hourly", labelKey: "leave_sheet_duration_hourly" },
] as const;

const COMMENT_MAX_LENGTH = 1000;

// Both footers keep clear of the gesture bar of a phone (the safe area is 0 where there is none).
const FOOTER_CLS = "space-y-3 border-t border-gray-200 bg-white px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]";

// On a phone the targets are at least 44px high.
const CHIP = "rounded-full border px-3 py-1.5 text-sm font-medium max-md:min-h-11";

interface LeaveRequestSheetProps {
  companyId: string;
  /** The manager who signs after the person; null when there is none. */
  managerName: string | null;
  onClose: () => void;
  /** A request now exists (signed or not), so the page behind it should reload and show it. */
  onRequestCreated: (leaveRequest: { startDate: string; endDate: string }) => void;
}

/**
 * «Naujas prašymas»: a drawer on a wide screen and a full-screen sheet on a phone. Step one
 * picks the type and days and creates the request; step two shows its document to sign.
 */
export default function LeaveRequestSheet({
  companyId,
  managerName,
  onClose,
  onRequestCreated,
}: LeaveRequestSheetProps) {
  const { t } = useTranslation();
  const flow = useLeaveRequestFlow({
    companyId,
    onSigned: ({ leaveRequest, managerApprovalPending, afterFailedAttempt }) => {
      toast.success(t(signedMessageKey(managerApprovalPending, afterFailedAttempt)));
      onRequestCreated(leaveRequest);
      onClose();
    },
  });

  // A request that exists but was not signed is still in the list, so the page behind reloads to show it.
  const close = () => {
    if (flow.created) onRequestCreated(flow.created);
    onClose();
  };

  return (
    <Dialog open onClose={flow.isBusy ? () => undefined : close} className="relative z-[1000]">
      <DialogBackdrop className="fixed inset-0 bg-zinc-950/40" />
      <div className="fixed inset-0 flex justify-end">
        <DialogPanel className="flex h-full w-full flex-col bg-white shadow-xl md:w-[min(560px,100vw)]">
          <header className="flex items-center gap-3 border-b border-gray-200 px-4 py-3">
            <DialogTitle className="min-w-0 flex-1 truncate text-base font-semibold text-gray-900">
              {t("leave_sheet_title")}
            </DialogTitle>
            <button type="button" onClick={close} disabled={flow.isBusy} aria-label={t("close")} className={`${btnIconCls} max-md:size-11`}>
              <X className="size-5" aria-hidden="true" />
            </button>
          </header>
          {flow.created ? (
            <SignStep fileId={flow.created.fileId} companyId={companyId} flow={flow} />
          ) : (
            <FormStep companyId={companyId} managerName={managerName} flow={flow} />
          )}
        </DialogPanel>
      </div>
    </Dialog>
  );
}

/** The toast after signing. A plain "ok" from the server after an earlier failed try does not say whether the manager was asked. */
function signedMessageKey(managerApprovalPending: boolean, afterFailedAttempt: boolean): string {
  if (managerApprovalPending) return "leave_sheet_submitted_waiting";
  if (afterFailedAttempt) return "leave_sheet_submitted";
  return "leave_sheet_submitted_done";
}

type Flow = ReturnType<typeof useLeaveRequestFlow>;

function FormStep({ companyId, managerName, flow }: { companyId: string; managerName: string | null; flow: Flow }) {
  const { t } = useTranslation();
  const locale = leaveCalendarDateLocale(useLanguage());
  const { picked } = flow;

  return (
    <>
      <div className="flex-1 space-y-5 overflow-y-auto px-4 py-4">
        <TypeChips selected={flow.leaveType} onChoose={flow.chooseType} />
        {flow.hasNoTemplate && <Notice tone="warning">{t("leave_sheet_no_template")}</Notice>}
        {flow.templates.length > 1 && <TemplateChoice flow={flow} />}

        <div>
          <RangeCalendar
            month={flow.month}
            picked={picked}
            isDayDisabled={flow.isDayDisabled}
            canGoToPreviousMonth={flow.canGoToPreviousMonth}
            onTapDay={flow.pickDay}
            onChangeMonth={flow.changeMonth}
          />
          <p className="mt-3 text-sm font-medium text-gray-900">
            {picked
              ? t("leave_sheet_picked", {
                  dates: rangeLabel(picked.start, picked.end, locale),
                  days:
                    flow.workingDays === null
                      ? t("leave_sheet_counting")
                      : t("leave_sheet_working_days", { n: flow.workingDays }),
                })
              : t("leave_sheet_pick_days")}
          </p>
          {flow.problemKey && (
            <p className="mt-1 text-sm text-red-600">{t(flow.problemKey, { days: MAX_LEAVE_DAYS })}</p>
          )}
        </div>

        {flow.isOneDay && <DurationFields flow={flow} />}

        {flow.templateFields.hasUnfillableField && (
          <Notice tone="warning">{t("leave_sheet_fields_unsupported")}</Notice>
        )}
        <CustomFieldInputs
          companyId={companyId}
          fields={flow.templateFields.list}
          errors={flow.templateFields.errors}
          onChange={flow.templateFields.setValue}
        />

        <div>
          <label htmlFor="leave-sheet-comment" className={FIELD_LABEL_CLS}>
            {t("leave_calendar_modal_comment")}
          </label>
          <textarea
            id="leave-sheet-comment"
            className={inputCls}
            rows={3}
            maxLength={COMMENT_MAX_LENGTH}
            value={flow.comment}
            onChange={(event) => flow.setComment(event.target.value)}
          />
          <p className={clsx(helpTextCls, "text-right")}>
            {flow.comment.length}/{COMMENT_MAX_LENGTH}
          </p>
        </div>

        <p className="text-sm text-gray-600">
          {managerName ? t("leave_sheet_signer_manager", { name: managerName }) : t("leave_sheet_signer_only_you")}
        </p>
      </div>

      <footer className={FOOTER_CLS}>
        {flow.errorKey && <Notice tone="critical">{t(flow.errorKey, { days: MAX_LEAVE_DAYS })}</Notice>}
        <button type="button" className={`${btnPrimaryCls} w-full max-md:min-h-11`} disabled={!flow.canSubmit} onClick={flow.submit}>
          {t("leave_sheet_submit")}
        </button>
      </footer>
    </>
  );
}

function TypeChips({ selected, onChoose }: { selected: string; onChoose: (type: string) => void }) {
  const { t } = useTranslation();
  // The other types open by themselves when one of them is chosen.
  const [showOthers, setShowOthers] = useState(OTHER_TYPES.includes(selected));
  const chip = (type: string) => (
    <button
      key={type}
      type="button"
      aria-pressed={type === selected}
      onClick={() => onChoose(type)}
      className={clsx(
        CHIP,
        type === selected
          ? "border-indigo-600 bg-indigo-600 text-white"
          : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"
      )}
    >
      {t(leaveCalendarLeaveTypeI18nKey(type), { defaultValue: type })}
    </button>
  );

  return (
    <div className="flex flex-wrap gap-2">
      {MAIN_TYPES.map(chip)}
      <button
        type="button"
        aria-expanded={showOthers}
        onClick={() => setShowOthers((open) => !open)}
        className={`${CHIP} inline-flex items-center gap-1 border-zinc-300 text-zinc-700 hover:bg-zinc-50`}
      >
        {t("leave_sheet_other_types")}
        <ChevronDown className={clsx("size-4", showOthers && "rotate-180")} aria-hidden="true" />
      </button>
      {showOthers && OTHER_TYPES.map(chip)}
    </div>
  );
}

/** Several templates for one type: the person chooses which request form to fill in. */
function TemplateChoice({ flow }: { flow: Flow }) {
  const { t } = useTranslation();
  return (
    <fieldset>
      <legend className={FIELD_LABEL_CLS}>{t("leave_sheet_template")}</legend>
      <div className="space-y-2">
        {flow.templates.map((candidate) => (
          <label
            key={candidate.id}
            className="flex cursor-pointer items-center gap-2 rounded-lg border border-zinc-300 px-3 py-2 text-sm text-gray-900 has-[:checked]:border-indigo-600 has-[:checked]:bg-indigo-50 max-md:min-h-11"
          >
            <input
              type="radio"
              name="leave-sheet-template"
              checked={candidate.id === flow.template?.id}
              onChange={() => flow.chooseTemplate(candidate.id)}
            />
            {candidate.name}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** Whole day, half a day or hours: only offered for a single day. Half a day and hours also ask for the times. */
function DurationFields({ flow }: { flow: Flow }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <div
        role="radiogroup"
        aria-label={t("leave_calendar_duration_label")}
        className="inline-flex rounded-lg bg-gray-100 p-0.5"
      >
        {DURATIONS.map(({ value, labelKey }) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={flow.duration === value}
            onClick={() => flow.chooseDuration(value)}
            className={clsx(
              "rounded-md px-3 py-1.5 text-sm font-medium max-md:min-h-11",
              flow.duration === value ? "bg-white text-gray-900 shadow-sm" : "text-gray-600"
            )}
          >
            {t(labelKey)}
          </button>
        ))}
      </div>
      {flow.duration !== "full_day" && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="leave-sheet-start-time" className={FIELD_LABEL_CLS}>
              {t("leave_calendar_start_time")}
            </label>
            <input
              id="leave-sheet-start-time"
              type="time"
              className={inputCls}
              value={flow.times.start}
              onChange={(event) => flow.times.setStart(event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="leave-sheet-end-time" className={FIELD_LABEL_CLS}>
              {t("leave_calendar_end_time")}
            </label>
            <input
              id="leave-sheet-end-time"
              type="time"
              className={inputCls}
              value={flow.times.end}
              onChange={(event) => flow.times.setEnd(event.target.value)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function SignStep({ fileId, companyId, flow }: { fileId: string; companyId: string; flow: Flow }) {
  const { t } = useTranslation();
  return (
    <>
      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
        <p className="text-sm text-gray-700">{t("leave_sheet_sign_hint")}</p>
        {/* The preview fills its parent, so the parent has a fixed height; a new key starts it afresh per document. */}
        <div className="h-[60dvh] min-h-[20rem] overflow-hidden rounded-lg border border-gray-200">
          <PdfView key={fileId} id={fileId} companyId={companyId} />
        </div>
        <p className={helpTextCls}>{t("leave_sheet_unsigned_note")}</p>
      </div>
      <footer className={FOOTER_CLS}>
        {flow.errorKey && <Notice tone="critical">{t(flow.errorKey)}</Notice>}
        <button type="button" className={`${btnPrimaryCls} w-full max-md:min-h-11`} disabled={flow.isBusy} onClick={flow.sign}>
          {t("sign")}
        </button>
      </footer>
    </>
  );
}
